/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { compose } from 'recompose';
import ReactHTMLParser from 'react-html-parser';
import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { trackPage } from '../../../../helpers/tracking/common/commonTrackingHelpers';
import withPageView from '../../../../helpers/tracking/hocs/withPageView';
import { makeStyles } from '@material-ui/core/styles';
import { TextField, Button, Grid } from '@material-ui/core';
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled';
import PauseCircleFilledIcon from '@material-ui/icons/PauseCircleFilled';


const useStyles = makeStyles((theme) => ({
   recipientFormContainer: {
      paddingLeft: '2rem',
      paddingRight: '2rem'
   },
   letterIcon: {
     fontSize: 80,
     color: theme.palette.primary.main,
   },
   form: {
     width: '100%',
     maxWidth: '500px',
   },
   buttonGrid: {
     display: 'flex',
     justifyContent: 'center', 
     textAlign: 'center', 
     marginBottom: '3rem', 
     marginTop: '1rem'
   },
   bodyText: {
    fontFamily: 'Lato',
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: '18px',
    lineHeight: '26px',
    textAlign: 'center',
    letterSpacing: '0.01em',
   },
   labelText: {
    fontFamily: 'Lato',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    display: 'flex',
    alignItems: 'center',
    letterSpacing: '0.01em',
    '&::after': {
       content: "*",
       color: 'red',
    },
    '& span': {
      color: '#FF0000'
    }
   },
   viewBtn: {
    fontFamily: 'Lato !important',
    fontStyle: 'normal !important',
    fontWeight: 700,
    fontSize: '18px !important',
    lineHeight: '20px !important',
    display: 'flex !important',
    alignItems: 'center !important',
    textAlign: 'center !important',
    letterSpacing: '0.04em !important',
    width: '100% !important',
    height: '60px !important',
    textTransform: 'uppercase !important',
    backgroundColor: theme.palette.colorPrimaryAccountCtaButton
   },
   textField: {
    '& .mbp-account-MuiFormHelperText-root': {
       marginLeft: '0px',
       marginRight: '0px',
    },
   },
   customControls: {
    position: 'absolute',
    top: '40%',
    left: '40%',
    fontSize: '100px',
    color: 'white',
    opacity: 0.6,
       '@media screen and (max-width: 375px)': {
          fontSize: '50px',
          top: '42%',
          left: '44%',
       },
       '@media screen and (min-width: 376px) and (max-width: 600px)': {
          fontSize: '70px',
          top: '42%',
          left: '42%',
       },
    },
    videoContainer: {
       position: 'relative'
    },
    titleContainer: {
       paddingLeft: '2rem',
       paddingRight: '2rem',
       paddingBottom: '1rem',
       paddingTop: '1rem',
    },
    displaynone: {
       display: 'none!important'
    }
 }));

const RecipientVerifyForm = ({ fetchRecipientData, isLoading }) => {
  const classes = useStyles();
  const videoRef = useRef(null);
  const dispatch = useDispatch();

  const [showButton, setShowButton] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [formData, setFormData] = useState({ 
     trackingNumber: '', 
     lastName: '', 
     errors: { 
       trackingNumber: '',
       lastName: '',
     }
  });
  
  const pageTitle = 'To open your e-card, please provide us with the tracking number (TRK#) on your package:';
  const formatPageTitle = pageTitle.split(',').join(', <br/>');
  const headerclip = 'https://assets.contentstack.io/v3/assets/bltdd99f24e8a94d536/bltc7d009653477aa60/6426ee8b8f4a131142f016f6/headerclip.mp4';
  const headerPoster = 'https://images.contentstack.io/v3/assets/bltdd99f24e8a94d536/blt3dca8e50373311d3/64342e2e7c179f10ee7cc8c0/videoposter.svg';

  const formatTrackingNumber = (trackingNumber) => {
    return trackingNumber.replace(/^(\d{2})(\d+)/, "$1 $2");
  };
 
  const isTrackingNumberValid = (trackingNumber) => {
    return /^\d{2}\s\d{4}$/.test(trackingNumber);
  };
 
  const isLastNameValid = (lastName) => {
    return /^[A-Za-z]+$/.test(lastName);
  };
 
  const handleInputChange = (e) => {
   const { name, value } = e.target;
   const updatedErrors = { ...formData.errors };

   if (name === 'trackingNumber') {
      setFormData({
         ...formData,
         [name]: formatTrackingNumber(value),
         errors: { ...updatedErrors, trackingNumber: '' },
      });
   } else {
      setFormData({ 
         ...formData, 
         [name]: value, 
         errors: { ...updatedErrors, lastName: '' },
      });
   }
  };

 const handleSubmit = (e) => {
   e.preventDefault();
 
   const { trackingNumber, lastName } = formData;
   const updatedErrors = {};
 
   if (!isTrackingNumberValid(trackingNumber)) {
     updatedErrors.trackingNumber = 'Please enter the last 6 digits of your Tracking Number';
   };

   if (!isLastNameValid(lastName)) {
     updatedErrors.lastName = 'This field is required';
   };

   if (Object.keys(updatedErrors).length === 0) {
     fetchRecipientData(formData);
     dispatch(trackEvent({
       eventCategory: 'Recipient Verify Form',
       eventAction: 'Click View E-Card',
       eventLabel: 'Recipient Verify Form',
     }));
   } else {
     setFormData({ ...formData, errors: { ...formData.errors, ...updatedErrors }});
   }
  };
 

  const togglePlay = (e) => {
   e.preventDefault();
   const video = videoRef.current;
   setShowButton(true);
   if (isPlaying) {
      video.pause();
   } else {
      video.play();
   };
   setIsPlaying(!isPlaying);
  };

  const handleDisplayButton = (status) => {
   if (typeof status !== 'undefined') {
      setShowButton(status)
   } else if (videoRef.current.currentTime > 0 && !videoRef.current.paused && videoRef.current.currentTime !== videoRef.current.duration) {
      setIsPlaying(true);
      setShowButton(true);
   } else {
      setIsPlaying(false);
      setShowButton(true);
   };
  };

  return (
   <React.Fragment>
      <Grid item className={classes.videoContainer} onClick={(e) => togglePlay(e)} onMouseEnter={() => handleDisplayButton(true)} onMouseLeave={() => handleDisplayButton(false)} onMouseMove={() => handleDisplayButton()}>
            <video onEnded={() => { setShowButton(true); setIsPlaying(false) }} poster={headerPoster} playsInline type="video/mp4" height="100%" width="100%" controlsList="nodownload" ref={videoRef} autoPlay muted>
               <source src={headerclip} />
            </video>
            {showButton && (isPlaying 
               ? <PauseCircleFilledIcon className={classes.customControls} />
               : <PlayCircleFilledIcon className={classes.customControls} />
            )}
      </Grid>
      <Grid item className={classes.titleContainer}>
         <p className={classes.bodyText} align="center">
            {ReactHTMLParser(formatPageTitle)}
         </p>
      </Grid>
      <Grid item className={classes.recipientFormContainer}>
         <form onSubmit={handleSubmit} className={classes.form}>
            <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
               <Grid item className={classes.labelText}>Tracking Number (last 6 digits only)<span>&#42;</span></Grid>
               <TextField
                  name="trackingNumber"
                  className={classes.textField}
                  variant="outlined"
                  placeholder='XX XXXX'
                  type="text"
                  value={formData.trackingNumber}
                  onChange={handleInputChange}
                  fullWidth
                  error={Boolean(formData.errors.trackingNumber)}
                  helperText={formData.errors.trackingNumber}
                  inputProps={{ maxLength: 7 }}
                  isrequired="true"
               />
            </Grid>
            <Grid item xs={12}>
               <Grid item className={classes.labelText}>Last Name<span>&#42;</span></Grid>
               <TextField
                  name="lastName"
                  className={classes.textField}
                  variant="outlined"
                  placeholder=''
                  type="text"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  fullWidth
                  error={Boolean(formData.errors.lastName)}
                  helperText={formData.errors.lastName}
                  inputProps={{ minLength: 1 }}
                  isrequired="true"
                  autoComplete="off"
               />
            </Grid>
            <Grid item xs={12} className={classes.buttonGrid}>
               <Button 
                  className={classes.viewBtn} type="submit" disabled={isLoading}>
                  {isLoading ? 'Loading...' : 'VIEW E-CARD'}
               </Button>
            </Grid>
            </Grid>
         </form>
      </Grid>
  </React.Fragment>
  )
}

const enhance = compose(
   trackPage({
      type: 'recipientverifyform',
      name: 'recipient verify form',
      accountPage: true,
   }),
   withPageView({
      pageViewSuccessTest: () => true,
   }),
);

export default enhance(RecipientVerifyForm);